/**
 * Enhanced Job Recommendation Algorithms
 * This file contains algorithms for personalized job recommendations based on VA profile, history, and behavior
 */

import { createSkillVector, createWeightedSkillVector, extractSkillNames, cosineSimilarity } from './skillUtils';

/**
 * Weight factors for different recommendation signals
 */
const WEIGHT_FACTORS = {
  SKILL_MATCH: 0.40,        // 40% weight for skill matching
  INDUSTRY_MATCH: 0.15,     // 15% weight for industry preferences
  RATE_MATCH: 0.15,         // 15% weight for rate matching
  ENGAGEMENT_HISTORY: 0.20, // 20% weight for past engagement
  RECENCY: 0.10             // 10% weight for job posting recency
};

/**
 * Calculate a personalized score between a VA profile and a job
 * @param {Object} vaProfile - The VA's complete profile
 * @param {Object} job - A job posting
 * @param {Array} allSkills - Master list of all available skills
 * @param {Object} vaEngagementHistory - VA's past interactions with jobs
 * @return {number} - Personalized match score (0-100)
 */
export const calculatePersonalizedMatchScore = (vaProfile, job, allSkills, vaEngagementHistory = {}) => {
  // 1. Enhanced Skill Match Score (using new up-skills format)
  const vaSkills = vaProfile?.skills || [];
  const jobSkills = job.skills_required || [];
  
  // Extract skill names for both VA and job (handles both string and object formats)
  const vaSkillNames = extractSkillNames(vaSkills);
  const jobSkillNames = extractSkillNames(jobSkills);
  
  // Create skill vectors for basic matching
  const vaVector = createSkillVector(vaSkills, allSkills);
  const jobVector = createSkillVector(jobSkills, allSkills);
  
  // Enhanced matching: Use weighted vectors if VA has up-skills format
  const hasUpSkillsFormat = Array.isArray(vaSkills) && vaSkills.some(skill => 
    typeof skill === 'object' && skill !== null && skill.rating
  );
  
  let skillScore;
  if (hasUpSkillsFormat) {
    // Use weighted matching that considers skill ratings
    const vaWeightedVector = createWeightedSkillVector(vaSkills, allSkills);
    skillScore = cosineSimilarity(vaWeightedVector, jobVector);
    
    // Bonus for high-rated skills that match job requirements
    const matchingSkills = vaSkillNames.filter(skill => jobSkillNames.includes(skill));
    const highRatedMatches = Array.isArray(vaSkills) ? vaSkills.filter(skill => 
      typeof skill === 'object' && 
      skill.rating >= 4 && 
      matchingSkills.includes(skill.name)
    ).length : 0;
    
    if (highRatedMatches > 0) {
      skillScore = Math.min(skillScore + (highRatedMatches * 0.1), 1.0);
    }
  } else {
    // Use basic matching for legacy format
    skillScore = cosineSimilarity(vaVector, jobVector);
  }
  
  // 2. Industry Match Score
  let industryScore = 0;
  if (vaProfile.industry_specialization && job.industry) {
    // Convert industry_specialization to array if it's a string
    const industries = Array.isArray(vaProfile.industry_specialization) 
      ? vaProfile.industry_specialization 
      : [vaProfile.industry_specialization];
    
    // Check if any of the VA's industries match the job industry
    if (industries.includes(job.industry)) {
      industryScore = 1;
    } else if (industries.includes('Technology') && job.industry === 'IT') {
      industryScore = 0.8;
    } else {
      industryScore = 0.1;
    }
  }
  
  // 3. Rate Match Score
  let rateScore = 0;
  if (vaProfile.minimum_rate && vaProfile.maximum_rate && job.hourly_rate) {
    const minRate = parseFloat(vaProfile.minimum_rate);
    const maxRate = parseFloat(vaProfile.maximum_rate);
    const jobRate = parseFloat(job.hourly_rate);
    
    // Perfect if job rate is within VA's desired range
    if (jobRate >= minRate && jobRate <= maxRate) {
      rateScore = 1;
    } 
    // Diminishing score for rates outside the VA's range
    else if (jobRate > maxRate) {
      // Higher than max is still good (opportunity for more money)
      const overRatio = Math.min(jobRate / maxRate, 2);
      rateScore = Math.max(0, 1 - (overRatio - 1) * 0.5);
    } else {
      // Lower than min is less desirable
      const underRatio = Math.min(minRate / jobRate, 2);
      rateScore = Math.max(0, 1 - (underRatio - 1) * 0.8);
    }
  }
  
  // 4. Engagement History Score
  let engagementScore = 0;
  if (vaEngagementHistory) {
    // Check if VA has previously engaged with similar jobs
    const jobType = job.job_type || '';
    const similarJobEngagements = Object.values(vaEngagementHistory)
      .filter(history => history.jobType === jobType)
      .length;
      
    // Calculate score based on engagement with similar job types
    engagementScore = Math.min(similarJobEngagements / 3, 1);
    
    // Boost score if they've applied to jobs from this employer before
    if (Object.values(vaEngagementHistory).some(history => 
        history.employerId === job.employer_id && history.action === 'applied')) {
      engagementScore = Math.min(engagementScore + 0.2, 1);
    }
  }
  
  // 5. Recency Score (newer jobs get higher scores)
  let recencyScore = 0;
  if (job.posted_at) {
    const postedDate = new Date(job.posted_at);
    const now = new Date();
    const daysSincePosted = (now - postedDate) / (1000 * 60 * 60 * 24);
    
    // Jobs less than 3 days old get highest score
    if (daysSincePosted <= 3) {
      recencyScore = 1;
    } 
    // Score decreases as job gets older, with 30 days as baseline for lowest score
    else {
      recencyScore = Math.max(0, 1 - (daysSincePosted - 3) / 27);
    }
  }
  
  // Calculate weighted composite score
  const compositeScore = 
    skillScore * WEIGHT_FACTORS.SKILL_MATCH +
    industryScore * WEIGHT_FACTORS.INDUSTRY_MATCH +
    rateScore * WEIGHT_FACTORS.RATE_MATCH +
    engagementScore * WEIGHT_FACTORS.ENGAGEMENT_HISTORY +
    recencyScore * WEIGHT_FACTORS.RECENCY;
  
  // Convert to percentage (0-100) and round to 1 decimal place
  return parseFloat((compositeScore * 100).toFixed(1));
};

/**
 * Get personalization reasons for why a job was recommended
 * @param {Object} vaProfile - The VA's complete profile
 * @param {Object} job - A job posting
 * @param {number} matchScore - The calculated match score
 * @return {Array} - Array of reasons for recommendation
 */
export const getRecommendationReasons = (vaProfile, job, matchScore) => {
  const reasons = [];
  
  // Enhanced skill match reasons (handles both formats)
  const vaSkills = vaProfile?.skills || [];
  const jobSkills = job.skills_required || [];
  
  // Extract skill names from both formats
  const vaSkillNames = extractSkillNames(vaSkills);
  const jobSkillNames = extractSkillNames(jobSkills);
  const matchingSkills = vaSkillNames.filter(skill => jobSkillNames.includes(skill));
  
  if (matchingSkills.length > 0) {
    // Check if user has high-rated skills that match
    const hasUpSkillsFormat = Array.isArray(vaSkills) && vaSkills.some(skill => 
      typeof skill === 'object' && skill !== null && skill.rating
    );
    
    if (hasUpSkillsFormat) {
      const highRatedMatches = Array.isArray(vaSkills) ? vaSkills.filter(skill => 
        typeof skill === 'object' && 
        skill.rating >= 4 && 
        matchingSkills.includes(skill.name)
      ) : [];
      
      if (highRatedMatches.length > 0) {
        const skillNames = highRatedMatches.slice(0, 2).map(s => s.name).join(', ');
        reasons.push(`Strong expertise in: ${skillNames}`);
      } else if (matchingSkills.length > 3) {
        reasons.push(`You have ${matchingSkills.length} required skills for this job`);
      } else {
        reasons.push(`Matches your skills: ${matchingSkills.slice(0, 3).join(', ')}`);
      }
    } else {
      // Legacy format handling
      if (matchingSkills.length > 3) {
        reasons.push(`You have ${matchingSkills.length} required skills for this job`);
      } else {
        reasons.push(`Matches your skills: ${matchingSkills.slice(0, 3).join(', ')}`);
      }
    }
  }
  
  // Industry match
  if (vaProfile.industry_specialization) {
    const industries = Array.isArray(vaProfile.industry_specialization) 
      ? vaProfile.industry_specialization 
      : [vaProfile.industry_specialization];
    
    if (industries.includes(job.industry)) {
      reasons.push(`Matches your preferred industry: ${job.industry}`);
    }
  }
  
  // Rate match
  if (vaProfile.minimum_rate && vaProfile.maximum_rate && job.hourly_rate) {
    const minRate = parseFloat(vaProfile.minimum_rate);
    const maxRate = parseFloat(vaProfile.maximum_rate);
    const jobRate = parseFloat(job.hourly_rate);
    
    if (jobRate >= minRate && jobRate <= maxRate) {
      reasons.push(`Rate matches your preferred range: $${jobRate}/hr`);
    } else if (jobRate > maxRate) {
      reasons.push(`Rate higher than your preferred maximum: $${jobRate}/hr`);
    }
  }
  
  // High match score
  if (matchScore > 90) {
    reasons.push('Exceptional match for your profile');
  } else if (matchScore > 80) {
    reasons.push('Great match for your profile');
  }
  
  // If job is recent
  if (job.posted_at) {
    const postedDate = new Date(job.posted_at);
    const now = new Date();
    const hoursSincePosted = (now - postedDate) / (1000 * 60 * 60);
    
    if (hoursSincePosted < 24) {
      reasons.push('New job posted today');
    } else if (hoursSincePosted < 48) {
      reasons.push('New job posted yesterday');
    }
  }
  
  return reasons.slice(0, 2); // Return top 2 reasons
};
